import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, FalhaDaApi } from '../api/cliente';
import {
  Alerta,
  Botao,
  Carregando,
  Cartao,
  Ficha,
  TituloDeSecao,
  Vazio,
} from '../componentes/base';
import { Seletor } from '../componentes/campos';
import { ModalAlterarParcela, type ParcelaParaAlterar } from '../componentes/AlterarParcela';
import { ModalDarBaixa, type ParcelaParaBaixar } from '../componentes/DarBaixa';
import { ModalFirmarAcordo } from '../componentes/FirmarAcordo';
import { AcordosAnteriores } from '../componentes/AcordosAnteriores';
import { ModalRomperAcordo } from '../componentes/RomperAcordo';
import { AtualizacoesDoCaso } from '../componentes/AtualizacoesDoCaso';
import { DocumentosDoCaso } from '../componentes/DocumentosDoCaso';
import { ExcluirDevedor } from '../componentes/ExcluirDevedor';
import { ProcessoDoCaso } from '../componentes/ProcessoDoCaso';
import {
  data,
  dataHora,
  documento,
  moeda,
  percentual,
  plural,
  telefone,
} from '../util/formato';

interface Parcela {
  readonly id: string;
  readonly numero: number;
  readonly valor: number;
  readonly vencimento: string;
  readonly status: string;
  readonly pagamento: {
    valorPago: number;
    honorarios: number;
    permaneceComClinica: number;
    percentualHonorarios: number;
    dataPagamento: string;
    formaPagamento: string;
    observacoes: string | null;
    comprovante: string | null;
  } | null;
}

interface Acordo {
  readonly id: string;
  readonly tipo: string;
  readonly status: string;
  readonly valorTotal: number;
  readonly quantidadeParcelas: number;
  readonly percentualMulta: number | null;
  readonly totalPago: number;
  readonly saldoDevedor: number;
  readonly parcelas: Parcela[];
  readonly seRescindidoHoje: {
    temClausulaPenal: boolean;
    saldoEmAberto: number;
    valorDaMulta: number | null;
    totalExigivel: number;
  };
}

interface FichaCompleta {
  readonly id: string;
  readonly nome: string;
  readonly documento: string | null;
  readonly documentoValido: boolean;
  readonly telefone: string | null;
  readonly email: string | null;
  readonly cidade: string | null;
  readonly uf: string | null;
  readonly valorOriginal: number;
  readonly descricaoDivida: string | null;
  readonly pastaDocumentos: string | null;
  readonly fase: string;
  readonly aptoJudicial: boolean;
  readonly clinica: {
    id: string;
    nome: string;
    percentualHonorarios: number;
    /** Carteira que usa as cinco fases de acompanhamento. */
    fasesDeAcompanhamento: boolean;
  };
  readonly responsavel: { id: string; nome: string } | null;
  readonly acordos: Acordo[];
  readonly interacoes: Array<{
    id: string;
    data: string;
    tipo: string;
    descricao: string;
    criadoEm: string;
    autor: { nome: string } | null;
    autorExterno: string | null;
  }>;
  readonly documentos: Array<{
    id: string;
    tipo: string;
    titulo: string | null;
    nomeOriginal: string;
    assinado: boolean;
    conferido: boolean;
  }>;
  readonly processos: ProcessoDoCaso[];
}

const SITUACAO_DA_PARCELA: Record<string, { rotulo: string; cor: string }> = {
  PAGO: { rotulo: 'Paga', cor: 'var(--color-recebido)' },
  ATRASADO: { rotulo: 'Em atraso', cor: 'var(--color-vencido)' },
  PENDENTE: { rotulo: 'A vencer', cor: 'var(--texto-suave)' },
  CANCELADO: { rotulo: 'Cancelada', cor: 'var(--texto-tenue)' },
};

export function PaginaFichaDevedor() {
  const { id = '' } = useParams();
  const clienteConsulta = useQueryClient();
  const [aviso, setAviso] = useState<{ texto: string; tom: 'recebido' | 'vencido' } | null>(null);

  const { data: ficha, isLoading } = useQuery({
    queryKey: ['devedor', id],
    queryFn: () => api.buscar<FichaCompleta>(`/devedores/${id}`),
    enabled: Boolean(id),
  });

  const { data: fases } = useQuery({
    queryKey: ['fases'],
    queryFn: () =>
      api.buscar<{
        fases: Array<{
          chave: string;
          curto: string;
          rotulo: string;
          cor: string;
          acompanhamento?: boolean;
        }>;
      }>('/devedores/fases'),
    staleTime: Infinity,
  });

  function recarregar() {
    void clienteConsulta.invalidateQueries({ queryKey: ['devedor', id] });
    void clienteConsulta.invalidateQueries({ queryKey: ['painel'] });
  }

  /* A baixa passou a ser um diálogo: o devedor nem sempre paga o valor combinado,
     e o honorário do escritório é calculado sobre o que ENTROU. Registrar sempre o
     valor da parcela faria a prestação de contas mentir. */
  const [baixando, setBaixando] = useState<ParcelaParaBaixar | null>(null);
  const [firmando, setFirmando] = useState(false);
  const [rompendo, setRompendo] = useState(false);
  const [renegociando, setRenegociando] = useState(false);
  const [alterando, setAlterando] = useState<
    (ParcelaParaAlterar & { totalDoAcordo: number }) | null
  >(null);

  const estornar = useMutation({
    mutationFn: (parcelaId: string) => api.criar(`/parcelas/${parcelaId}/estorno`, {}),
    onSuccess: () => {
      setAviso({ texto: 'Baixa estornada.', tom: 'recebido' });
      recarregar();
    },
    onError: (erro) =>
      setAviso({
        texto: erro instanceof FalhaDaApi ? erro.message : 'Não foi possível estornar.',
        tom: 'vencido',
      }),
  });

  const mudarFase = useMutation({
    mutationFn: (fase: string) => api.criar(`/devedores/${id}/fase`, { fase }),
    onSuccess: recarregar,
    /* Sem isto, a fase recusada pelo servidor voltava sozinha no seletor e nada
       explicava por quê — o seletor é controlado pela ficha, então a impressão é
       de que o clique não pegou. */
    onError: (erro) =>
      setAviso({
        texto:
          erro instanceof FalhaDaApi ? erro.message : 'Não foi possível mudar a fase.',
        tom: 'vencido',
      }),
  });

  if (isLoading || !ficha) return <Carregando />;

  const faseAtual = fases?.fases.find((f) => f.chave === ficha.fase);

  /* "Em dia", "em atraso", "a vencer" e as duas de acordo só fazem sentido onde a
     carteira é de acordo parcelado e a fase, sozinha, parou de distinguir os casos.
     Nas demais elas só alongariam uma lista que já tem dez opções.

     A fase ATUAL entra sempre, mesmo fora da lista da carteira: se alguém desligar
     a opção na clínica com casos já nessas fases, o seletor tem de continuar
     mostrando onde o caso está — senão ele apareceria vazio, e salvar mudaria a
     fase sem ninguém pedir. */
  const fasesOferecidas = (fases?.fases ?? []).filter(
    (f) => !f.acompanhamento || ficha.clinica.fasesDeAcompanhamento || f.chave === ficha.fase,
  );
  const acordoAtivo = ficha.acordos.find((a) => a.status === 'ATIVO') ?? ficha.acordos[0];

  return (
    <>
      <Link
        to="/devedores"
        className="mb-3 inline-block text-xs text-[color:var(--texto-tenue)] hover:text-[color:var(--acento)]"
      >
        ← Carteira de devedores
      </Link>

      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-[1.75rem] leading-tight">{ficha.nome}</h1>
            {faseAtual && <Ficha cor={faseAtual.cor}>{faseAtual.rotulo}</Ficha>}
            {ficha.aptoJudicial && <Ficha tom="recebido">Apto ao ajuizamento</Ficha>}
          </div>
          <p className="mt-1.5 text-sm text-[color:var(--texto-suave)]">
            <span className="font-mono">{documento(ficha.documento)}</span>
            {ficha.documento && !ficha.documentoValido && (
              <span style={{ color: 'var(--color-atencao)' }}> · CPF suspeito</span>
            )}
            {' · '}
            <span className="font-mono">{telefone(ficha.telefone)}</span>
            {' · '}
            {ficha.clinica.nome} ({percentual(ficha.clinica.percentualHonorarios)} de êxito)
          </p>
          {ficha.descricaoDivida && (
            <p className="mt-1 text-sm text-[color:var(--texto-tenue)]">
              {ficha.descricaoDivida} — dívida de origem {moeda(ficha.valorOriginal)}
            </p>
          )}
          {/* O contrato mora no Drive da clínica. Aqui fica o caminho até ele. */}
          {ficha.pastaDocumentos && (
            <p className="mt-1.5 text-sm">
              <a
                href={ficha.pastaDocumentos}
                target="_blank"
                rel="noreferrer"
                className="text-[color:var(--acento)] underline underline-offset-2"
              >
                Pasta no Drive →
              </a>
            </p>
          )}
        </div>

        {/* As duas ações que valem para o caso INTEIRO ficam juntas aqui no alto.
            A clínica não exclui cliente pelo portal — ela cadastra e anexa; quem
            tira do sistema é o escritório. */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-56 max-w-full">
            <Seletor
              rotulo="Mover para a fase"
              valor={ficha.fase}
              aoMudar={(v) => mudarFase.mutate(v)}
              opcoes={fasesOferecidas.map((f) => ({ valor: f.chave, rotulo: f.curto }))}
            />
          </div>
          <ExcluirDevedor devedorId={ficha.id} nome={ficha.nome} />
        </div>
      </header>

      {aviso && (
        <div className="mb-5">
          <Alerta tom={aviso.tom}>{aviso.texto}</Alerta>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.6fr_1fr]">
        <div className="flex flex-col gap-6">
          {/* ─────────────────────────────────────────────── Acordo ── */}
          {acordoAtivo ? (
            <Cartao semPadding>
              <div className="p-5 pb-3">
                {/* AS AÇÕES DO ACORDO FICAM AQUI, no alto e ao lado do valor.
                    Ficavam dentro da faixa de rescisão, em letra miúda no fim de
                    uma frase — pareciam texto, não botão, e quem precisava
                    renegociar não as encontrava. O lugar de mudar o acordo é ao
                    lado do acordo. */}
                <div className="mb-1 flex flex-wrap items-start justify-between gap-3">
                  <TituloDeSecao
                    auxiliar={
                      acordoAtivo.status === 'ATIVO'
                        ? `${acordoAtivo.quantidadeParcelas}x · ativo`
                        : acordoAtivo.status.toLowerCase()
                    }
                  >
                    Acordo de {moeda(acordoAtivo.valorTotal)}
                  </TituloDeSecao>

                  {acordoAtivo.status === 'ATIVO' && (
                    <span className="flex shrink-0 items-center gap-2">
                      {/* ALTERAR antes de RESCINDIR, e é o caminho comum: o devedor
                          que liga dizendo "não consigo mais pagar esse valor" quer
                          refazer o acordo, não rompê-lo. */}
                      <Botao tipo="primario" onClick={() => setRenegociando(true)}>
                        Alterar acordo
                      </Botao>
                      <Botao onClick={() => setRompendo(true)}>Rescindir</Botao>
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
                  <span className="text-[color:var(--texto-suave)]">
                    Pago{' '}
                    <strong
                      className="tabular font-semibold"
                      style={{ color: 'var(--color-recebido)' }}
                    >
                      {moeda(acordoAtivo.totalPago)}
                    </strong>
                  </span>
                  <span className="text-[color:var(--texto-suave)]">
                    Saldo{' '}
                    <strong className="tabular font-semibold text-[color:var(--texto)]">
                      {moeda(acordoAtivo.saldoDevedor)}
                    </strong>
                  </span>
                  <span className="text-[color:var(--texto-suave)]">
                    Cláusula penal{' '}
                    <strong className="font-semibold text-[color:var(--texto)]">
                      {acordoAtivo.percentualMulta !== null
                        ? percentual(acordoAtivo.percentualMulta)
                        : 'não prevista'}
                    </strong>
                  </span>
                </div>

                {/* A projeção da rescisão fica à vista o tempo todo: é o número que
                    decide se vale insistir no acordo ou partir para o judicial. O
                    botão fica JUNTO dela — o número é a razão de apertá-lo, e
                    separá-los faria decidir num lugar e agir em outro. */}
                {acordoAtivo.status === 'ATIVO' &&
                  acordoAtivo.seRescindidoHoje.saldoEmAberto > 0 && (
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded border-l-[3px] bg-[color:var(--superficie-afundada)] px-3 py-2"
                       style={{ borderColor: 'var(--acento)' }}>
                      <p className="text-xs text-[color:var(--texto-suave)]">
                        Se rescindido hoje, seria exigível{' '}
                        <strong className="tabular font-semibold text-[color:var(--acento)]">
                          {moeda(acordoAtivo.seRescindidoHoje.totalExigivel)}
                        </strong>
                        {acordoAtivo.seRescindidoHoje.valorDaMulta !== null && (
                          <>
                            {' '}
                            — {moeda(acordoAtivo.seRescindidoHoje.saldoEmAberto)} de saldo mais{' '}
                            {moeda(acordoAtivo.seRescindidoHoje.valorDaMulta)} de multa
                          </>
                        )}
                        .
                      </p>
                    </div>
                  )}
              </div>

              <div className="rolagem-lateral border-t">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-[color:var(--superficie-afundada)] text-left text-[0.6875rem] uppercase tracking-[0.08em] text-[color:var(--texto-tenue)]">
                      <th className="px-5 py-2 font-medium">Parcela</th>
                      <th className="px-3 py-2 font-medium">Vencimento</th>
                      <th className="px-3 py-2 text-right font-medium">Valor</th>
                      <th className="px-3 py-2 font-medium">Situação</th>
                      <th className="px-3 py-2 text-right font-medium">Honorários</th>
                      <th className="px-5 py-2 text-right font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {acordoAtivo.parcelas.map((p) => {
                      const situacao = SITUACAO_DA_PARCELA[p.status] ?? {
                        rotulo: p.status,
                        cor: 'var(--texto-suave)',
                      };
                      return (
                        <tr key={p.id} className="border-b last:border-0">
                          <td className="tabular px-5 py-2.5">
                            {p.numero === 0 ? 'Entrada' : p.numero}
                          </td>
                          <td className="tabular px-3 py-2.5 whitespace-nowrap text-[color:var(--texto-suave)]">
                            {data(p.vencimento)}
                          </td>
                          {/* `whitespace-nowrap`: sem isso o "R$" se separa do número
                              e a coluna de dinheiro deixa de ser conferível de relance. */}
                          <td className="tabular px-3 py-2.5 text-right whitespace-nowrap">
                            {moeda(p.valor)}
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <Ficha cor={situacao.cor}>{situacao.rotulo}</Ficha>
                            {p.pagamento && (
                              <span className="ml-2 text-[0.6875rem] text-[color:var(--texto-tenue)]">
                                em {data(p.pagamento.dataPagamento)}
                              </span>
                            )}
                          </td>
                          <td className="tabular px-3 py-2.5 text-right whitespace-nowrap">
                            {p.pagamento ? (
                              <span
                                className="font-medium"
                                style={{ color: 'var(--acento)' }}
                                title={`Congelado a ${percentual(p.pagamento.percentualHonorarios)} na data da baixa`}
                              >
                                {moeda(p.pagamento.honorarios)}
                              </span>
                            ) : (
                              <span className="text-[color:var(--texto-tenue)]">—</span>
                            )}
                          </td>
                          <td className="px-5 py-2.5 text-right">
                            {p.status === 'CANCELADO' ? null : p.pagamento ? (
                              <span className="flex items-center justify-end gap-2">
                                {/* "Registrei o valor errado" é o erro mais comum de
                                    todos. Sem isto, corrigir era estornar e relançar,
                                    redigitando data, forma e observação. */}
                                <Botao
                                  onClick={() =>
                                    setBaixando({
                                      id: p.id,
                                      numero: p.numero,
                                      valor: p.valor,
                                      vencimento: p.vencimento,
                                      /* Vai o que ESTÁ gravado, campo por campo. A
                                         forma vinha fixa em 'PIX' aqui, e o diálogo
                                         a manda sempre: corrigir o valor de uma
                                         transferência a transformava em PIX. */
                                      pagamento: {
                                        valorPago: p.pagamento!.valorPago,
                                        dataPagamento: p.pagamento!.dataPagamento,
                                        formaPagamento: p.pagamento!.formaPagamento,
                                        observacoes: p.pagamento!.observacoes,
                                        percentualHonorarios:
                                          p.pagamento!.percentualHonorarios,
                                      },
                                    })
                                  }
                                  className="px-2.5 py-1 text-xs"
                                >
                                  Corrigir
                                </Botao>
                                <Botao
                                  tipo="discreto"
                                  onClick={() => estornar.mutate(p.id)}
                                  desabilitado={estornar.isPending}
                                  className="px-0 text-xs"
                                >
                                  Estornar
                                </Botao>
                              </span>
                            ) : (
                              <span className="flex items-center justify-end gap-2">
                                <Botao
                                  tipo="discreto"
                                  onClick={() =>
                                    setAlterando({
                                      id: p.id,
                                      numero: p.numero,
                                      valor: p.valor,
                                      vencimento: p.vencimento,
                                      totalDoAcordo: acordoAtivo.valorTotal,
                                    })
                                  }
                                  className="px-0 text-xs"
                                >
                                  Alterar
                                </Botao>
                                <Botao
                                  onClick={() =>
                                    setBaixando({
                                      id: p.id,
                                      numero: p.numero,
                                      valor: p.valor,
                                      vencimento: p.vencimento,
                                    })
                                  }
                                  className="px-2.5 py-1 text-xs"
                                >
                                  Dar baixa
                                </Botao>
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Cartao>
          ) : (
            <Cartao>
              <Vazio
                titulo="Este caso ainda não tem acordo firmado."
                saida={
                  <Botao tipo="primario" onClick={() => setFirmando(true)}>
                    Firmar acordo
                  </Botao>
                }
              />
            </Cartao>
          )}

          {/* Renegociar cria um acordo novo e o anterior sai do cartão de cima. Sem
              isto, as parcelas que a pessoa PAGOU no acordo antigo sumiriam da ficha
              — e é justamente essa linha que responde "eu paguei, sim". */}
          <AcordosAnteriores acordos={ficha.acordos} />

          {/* ────────────────────────────────────── Linha do tempo ── */}
          <Cartao semPadding>
            <div className="p-5 pb-2">
              <TituloDeSecao auxiliar={`${ficha.interacoes.length} registros`}>
                Histórico do caso
              </TituloDeSecao>
            </div>
            <ol className="max-h-[30rem] overflow-y-auto">
              {ficha.interacoes.map((i) => (
                <li key={i.id} className="border-b px-5 py-2.5 last:border-0">
                  <p className="text-sm text-[color:var(--texto)]">{i.descricao}</p>
                  <p className="mt-0.5 text-[0.6875rem] text-[color:var(--texto-tenue)]">
                    {dataHora(i.criadoEm)} · {i.autor?.nome ?? i.autorExterno ?? 'Sistema'}
                  </p>
                </li>
              ))}
            </ol>
          </Cartao>
        </div>

        {/* ──────────────────────────────────────── Coluna lateral ── */}
        <div className="flex flex-col gap-6">
          <Cartao>
            <TituloDeSecao>Responsável</TituloDeSecao>
            <p className="text-sm text-[color:var(--texto)]">
              {ficha.responsavel?.nome ?? (
                <span className="text-[color:var(--texto-tenue)]">
                  Nenhuma colaboradora designada
                </span>
              )}
            </p>
          </Cartao>

          <Cartao semPadding>
            <DocumentosDoCaso devedorId={ficha.id} documentos={ficha.documentos} />
          </Cartao>

          {/* Logo abaixo dos documentos, de propósito: é a mesma pergunta — "o que
              já veio deste caso?" — respondida em arquivo e em texto. */}
          {/* Entre os documentos e as atualizações: as três coisas que a ficha
              responde sobre o caso — o que foi juntado, onde ele está na justiça
              e o que a equipe anotou. */}
          <Cartao semPadding>
            <ProcessoDoCaso devedorId={ficha.id} processos={ficha.processos} />
          </Cartao>

          <Cartao semPadding>
            <AtualizacoesDoCaso devedorId={ficha.id} interacoes={ficha.interacoes} />
          </Cartao>

          {ficha.acordos.length > 1 && (
            <Cartao>
              <TituloDeSecao>Acordos anteriores</TituloDeSecao>
              <ul className="flex flex-col gap-2 text-sm">
                {ficha.acordos.slice(1).map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-2">
                    <span className="tabular text-[color:var(--texto-suave)]">
                      {moeda(a.valorTotal)} em {plural(a.quantidadeParcelas, 'parcela', 'parcelas')}
                    </span>
                    <Ficha
                      tom={
                        a.status === 'QUITADO'
                          ? 'recebido'
                          : a.status === 'ROMPIDO'
                            ? 'vencido'
                            : 'neutro'
                      }
                    >
                      {a.status.toLowerCase()}
                    </Ficha>
                  </li>
                ))}
              </ul>
            </Cartao>
          )}
        </div>
      </div>

      <ModalFirmarAcordo
        aberto={firmando}
        aoFechar={() => setFirmando(false)}
        devedorId={ficha.id}
        nomeDoDevedor={ficha.nome}
        valorSugerido={ficha.valorOriginal}
      />

      <ModalFirmarAcordo
        aberto={renegociando}
        aoFechar={() => setRenegociando(false)}
        devedorId={ficha.id}
        nomeDoDevedor={ficha.nome}
        valorSugerido={ficha.valorOriginal}
        substituindo={
          acordoAtivo && acordoAtivo.status === 'ATIVO'
            ? {
                id: acordoAtivo.id,
                valorTotal: acordoAtivo.valorTotal,
                totalPago: acordoAtivo.totalPago,
                saldoEmAberto: acordoAtivo.seRescindidoHoje.saldoEmAberto,
                parcelasPagas: acordoAtivo.parcelas.filter((p) => p.pagamento).length,
              }
            : undefined
        }
      />

      <ModalRomperAcordo
        aberto={rompendo}
        aoFechar={() => setRompendo(false)}
        acordo={acordoAtivo && acordoAtivo.status === 'ATIVO' ? acordoAtivo : null}
        devedorId={ficha.id}
        nomeDoDevedor={ficha.nome}
      />

      <ModalAlterarParcela
        aberto={alterando !== null}
        aoFechar={() => setAlterando(null)}
        parcela={alterando}
        devedorId={ficha.id}
        totalDoAcordo={alterando?.totalDoAcordo ?? 0}
      />

      <ModalDarBaixa
        aberto={baixando !== null}
        aoFechar={() => setBaixando(null)}
        parcela={baixando}
        devedorId={ficha.id}
        nomeDoDevedor={ficha.nome}
        percentualHonorarios={ficha.clinica.percentualHonorarios}
      />
    </>
  );
}
